import { useCallback } from 'react';
import { useLanguageCode } from '@mews-commander/core';
import { useLegacyLocalization } from '@mews-commander/legacy-app-instance/localization';
import {
    useLocalizeDayName,
    useLocalizeDayShortName,
    useLocalizeLongDate,
    useLocalizeMonthName,
    useLocalizeMonthShortName,
} from '@mews-commander/ui';
import { createLocalizationProvider } from '@mews/framework';
import type { LocalizationHandlers } from './createLocalizationHandlers';

const BaseLocalizationProvider = createLocalizationProvider({
    localizationHandlerNames: [
        'localizeCurrencyValue',
        'localizeDate',
        'localizeDateTime',
        'localizeDayName',
        'localizeEnumify',
        'localizeLongDate',
        'localizeMonthName',
        'localizeNumber',
        'localizePercent',
        'localizeProperty',
        'localizeShortDate',
        'localizeShortDayName',
        'localizeShortMonthName',
        'localizeShortTime',
        'localizeWallClockDate',
    ] as const,
});

export interface LocalizationProviderProps {
    localizationHandlers: LocalizationHandlers;
    children: React.ReactNode;
}

const useInjectLangCode = <TFunction extends () => (...args: any[]) => string>(useLocalizeFn: TFunction) => {
    const localize = useLocalizeFn();
    return useCallback((lCode: string, ...args: Parameters<ReturnType<TFunction>>) => localize(...args), [localize]);
};

const LocalizationProvider = ({ localizationHandlers, ...props }: LocalizationProviderProps) => {
    const languageCode = useLanguageCode();
    useLegacyLocalization();

    return (
        <BaseLocalizationProvider
            languageCode={languageCode}
            localizationHandlers={{
                ...localizationHandlers,
                localizeLongDate: useInjectLangCode(useLocalizeLongDate),
                localizeDayName: useInjectLangCode(useLocalizeDayName),
                localizeShortDayName: useInjectLangCode(useLocalizeDayShortName),
                localizeMonthName: useInjectLangCode(useLocalizeMonthName),
                localizeShortMonthName: useInjectLangCode(useLocalizeMonthShortName),
            }}
            {...props}
        />
    );
};

export default () => LocalizationProvider;
